/**
 * Network resilience utilities
 * Provides exponential backoff, circuit breaker, and retry logic
 */

export interface RetryOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  retryableStatuses?: number[]
}

export interface CircuitBreakerState {
  failures: number
  lastFailureTime: number
  state: 'closed' | 'open' | 'half-open'
}

/**
 * Circuit Breaker implementation
 * Prevents cascading failures by stopping requests after too many failures
 */
class CircuitBreaker {
  private failures: number = 0
  private lastFailureTime: number = 0
  private state: 'closed' | 'open' | 'half-open' = 'closed'
  private readonly failureThreshold: number
  private readonly timeout: number
  private readonly resetTimeout: number

  constructor(
    failureThreshold: number = 5,
    resetTimeout: number = 60000 // 1 minute
  ) {
    this.failureThreshold = failureThreshold
    this.resetTimeout = resetTimeout
  }

  /**
   * Record a successful request
   */
  recordSuccess(): void {
    this.failures = 0
    this.state = 'closed'
  }

  /**
   * Record a failed request
   */
  recordFailure(): void {
    this.failures++
    this.lastFailureTime = Date.now()

    if (this.failures >= this.failureThreshold) {
      this.state = 'open'
    }
  }

  /**
   * Check if requests should be allowed
   */
  canAttempt(): boolean {
    if (this.state === 'closed') {
      return true
    }

    if (this.state === 'open') {
      // Check if reset timeout has passed
      if (Date.now() - this.lastFailureTime > this.resetTimeout) {
        this.state = 'half-open'
        return true
      }
      return false
    }

    // Half-open state: allow one attempt
    return true
  }

  /**
   * Get current state
   */
  getState(): CircuitBreakerState {
    return {
      failures: this.failures,
      lastFailureTime: this.lastFailureTime,
      state: this.state,
    }
  }

  /**
   * Reset circuit breaker
   */
  reset(): void {
    this.failures = 0
    this.lastFailureTime = 0
    this.state = 'closed'
  }
}

// Global circuit breaker instance (per endpoint)
const circuitBreakers = new Map<string, CircuitBreaker>()

/**
 * Get or create circuit breaker for an endpoint
 */
function getCircuitBreaker(endpoint: string): CircuitBreaker {
  if (!circuitBreakers.has(endpoint)) {
    circuitBreakers.set(endpoint, new CircuitBreaker())
  }
  return circuitBreakers.get(endpoint)!
}

/**
 * Calculate exponential backoff delay
 */
function calculateBackoffDelay(attempt: number, options: RetryOptions): number {
  const {
    initialDelay = 1000,
    maxDelay = 30000,
    backoffMultiplier = 2,
  } = options

  const delay = initialDelay * Math.pow(backoffMultiplier, attempt)
  return Math.min(delay, maxDelay)
}

/**
 * Check if HTTP status code is retryable
 */
function isRetryableStatus(status: number, retryableStatuses?: number[]): boolean {
  if (retryableStatuses) {
    return retryableStatuses.includes(status)
  }

  // Default: retry on 5xx, 408 (timeout), 429 (rate limit)
  return status >= 500 || status === 408 || status === 429
}

/**
 * Retry a fetch request with exponential backoff and circuit breaker
 */
export async function resilientFetch(
  url: string,
  options: RequestInit & { retryOptions?: RetryOptions } = {}
): Promise<Response> {
  const {
    retryOptions = {},
    ...fetchOptions
  } = options

  const {
    maxRetries = 3,
    retryableStatuses,
  } = retryOptions

  const circuitBreaker = getCircuitBreaker(url)
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Check circuit breaker
    if (!circuitBreaker.canAttempt()) {
      const state = circuitBreaker.getState()
      console.error('resilientFetch: Circuit breaker is open', url, state)
      throw new Error('Circuit breaker is open. Too many failures.')
    }

    try {
      if (process.env.NODE_ENV === "development" && attempt === 0) {
        console.log('resilientFetch: Attempting fetch', url, {
          attempt: attempt + 1,
          maxRetries: maxRetries + 1,
          method: fetchOptions.method || 'GET',
        })
      }
      
      // Perform fetch with timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15s timeout

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      })
      
      if (process.env.NODE_ENV === "development" && attempt === 0) {
        console.log('resilientFetch: Fetch completed', url, {
          status: response.status,
          ok: response.ok,
        })
      }

      clearTimeout(timeoutId)

      // Check if response is retryable
      if (!response.ok && isRetryableStatus(response.status, retryableStatuses)) {
        if (attempt < maxRetries) {
          // Record failure for circuit breaker
          circuitBreaker.recordFailure()

          // Calculate backoff delay
          const delay = calculateBackoffDelay(attempt, retryOptions)

          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, delay))
          continue
        }
      }

      // Success or non-retryable error
      if (response.ok) {
        circuitBreaker.recordSuccess()
      } else {
        circuitBreaker.recordFailure()
      }

      return response
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      if (process.env.NODE_ENV === "development") {
        console.error('resilientFetch: Fetch error', url, {
          attempt: attempt + 1,
          error: lastError.message,
          name: lastError.name,
          isNetworkError: error instanceof TypeError && error.message.includes('fetch'),
          isTimeoutError: error instanceof Error && error.name === 'AbortError',
        })
      }

      // Check if error is retryable
      const isNetworkError = error instanceof TypeError && error.message.includes('fetch')
      const isTimeoutError = error instanceof Error && error.name === 'AbortError'

      if ((isNetworkError || isTimeoutError) && attempt < maxRetries) {
        circuitBreaker.recordFailure()

        // Calculate backoff delay
        const delay = calculateBackoffDelay(attempt, retryOptions)

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      // Max retries reached or non-retryable error
      circuitBreaker.recordFailure()
      throw lastError
    }
  }

  // Should not reach here, but TypeScript needs it
  throw lastError || new Error('Max retries exceeded')
}

/**
 * Reset circuit breaker for an endpoint
 */
export function resetCircuitBreaker(endpoint: string): void {
  const circuitBreaker = getCircuitBreaker(endpoint)
  circuitBreaker.reset()
}

/**
 * Get circuit breaker state for an endpoint
 */
export function getCircuitBreakerState(endpoint: string): CircuitBreakerState {
  const circuitBreaker = getCircuitBreaker(endpoint)
  return circuitBreaker.getState()
}




